import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} SuperSaveBannerRefs
 * @property {HTMLElement} savedText
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} checkboxLabel
 * @property {HTMLElement} errorMessage
 */

/**
 * A custom element for the "Super Save" discount banner block. Lets the
 * shopper opt into a real Shopify discount code via an unticked-by-default
 * checkbox, applied/removed through the theme's standard AJAX cart-update
 * endpoint (the same mechanism the built-in cart discount field uses,
 * see assets/cart-discount.js), updates its own savings text live, and
 * best-effort mirrors the discount onto the nearby Product Price block's
 * displayed price.
 *
 * The click is optimistic: #render() runs immediately on click (checkbox
 * state is already flipped by the browser by the time the `change` event
 * fires), so the savings text, price, and the "+ ADDITIONAL X% OFF" badge
 * (rendered by the Flat Discount Badge block, see below) all update
 * instantly instead of waiting on the cart request's round trip. The real
 * discount application happens in the background and only reverts (with
 * an error message) if it genuinely fails.
 *
 * Only one discount code can be used per order: if the cart already holds a
 * different code that is really discounting it, ticking the box is refused
 * with a message.
 *
 * This block doesn't render its own "+ ADDITIONAL X% OFF" badge - that
 * lives in assets/flat-discount-badge.js so it can sit beside the FLAT
 * pill in one row. Every #render() dispatches a `supersave:toggle` custom
 * event (bubbled to the section) carrying the checked state, percentage,
 * and text template; the badge listens for it. Harmless with no listener.
 *
 * Text templates use square-bracket placeholders ([amount], [percentage])
 * rather than {{ }}, because Shopify's theme editor treats literal {{ }}
 * inside setting values as a "dynamic source" reference.
 *
 * @extends {Component<SuperSaveBannerRefs>}
 */
class SuperSaveBanner extends Component {
  requiredRefs = ['checkbox', 'savedText', 'checkboxLabel'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#readDataset();
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#render();
    // Sync the checkbox to whatever the cart actually has applied - see
    // #syncCheckboxWithCart() below for why this matters.
    this.#syncCheckboxWithCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#activeFetch?.abort();
  }

  #readDataset() {
    this.price = Number(this.dataset.price) || 0;
    this.compareAtPrice = Number(this.dataset.compareAtPrice) || 0;
    this.discountCode = this.dataset.discountCode || '';
    this.discountPercentage = Number(this.dataset.discountPercentage) || 0;
    this.currency = this.dataset.currency || 'USD';
    this.useCurrency = this.dataset.useCurrency === 'true';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.moneyWithCurrencyFormat = this.dataset.moneyWithCurrencyFormat || '${{amount}} {{currency}}';
    this.badgeTemplate = this.dataset.badgeTemplate || '+ ADDITIONAL [percentage]% OFF';
    this.savedTemplate = this.dataset.savedTemplate || '[amount] Saved!';
    this.checkboxTemplate = this.dataset.checkboxTemplate || 'Get an additional [amount] Off.';
  }

  /**
   * Shopify carts persist an applied discount code across page loads,
   * variant switches, and section re-renders - but until now this
   * component always started from a fresh, unticked checkbox with no
   * idea whether its discount code was already active on the cart. That
   * meant the checkbox could visibly read "unticked" while the discount
   * it controls was still live underneath it (e.g. after ticking it
   * once, then reloading the page, switching variants, or reopening the
   * theme editor preview) - which reads as the discount having applied
   * itself with no click. This checks the real cart on connect and
   * brings the checkbox's visual state back in line with it, without
   * changing anything about what's actually applied.
   *
   * A code that is in the cart but not discounting it (e.g. the order is
   * below the code's minimum) leaves the checkbox unticked.
   */
  async #syncCheckboxWithCart() {
    if (!this.discountCode) return;
    const { checkbox } = this.refs;
    if (!checkbox) return;

    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const alreadyApplied = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.some(
            (/** @type {{code: string, applicable: boolean}} */ dc) =>
              dc.code.toLowerCase() === this.discountCode.toLowerCase() &&
              (dc.applicable !== false || cart.item_count === 0)
          )
        : false;

      if (alreadyApplied !== checkbox.checked) {
        checkbox.checked = alreadyApplied;
        this.#render();
      }
    } catch {
      // Best-effort only: if this fails, the checkbox just keeps its
      // default unticked state and the shopper can still toggle it by hand.
    }
  }

  #format(cents) {
    const value = Math.max(0, Math.round(cents));
    const baseFormat = this.useCurrency ? this.moneyWithCurrencyFormat : this.moneyFormat;
    // Mirrors Shopify's `money_without_trailing_zeros` Liquid filter: when
    // the amount is a whole number in its major unit (no real cents to
    // lose - e.g. 99900 minor units = "999.00"), swap in the format's
    // no-decimals token so it reads "999" instead of "999.00". A
    // genuinely fractional amount keeps its decimals. Done by choosing
    // the token BEFORE formatting (not by regexing the output string)
    // specifically so thousands separators like the "," in "1,000.00"
    // can never be mistaken for the decimal point.
    const format = value % 100 === 0 ? this.#toNoDecimalsFormat(baseFormat) : baseFormat;
    return formatMoney(value, format, this.currency);
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #toNoDecimalsFormat(format) {
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, '{{amount_no_decimals_with_space_separator}}')
      .replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}');
  }

  #baseSaved() {
    return this.compareAtPrice > this.price ? this.compareAtPrice - this.price : 0;
  }

  #additionalSaved() {
    return Math.floor((this.price * this.discountPercentage) / 100);
  }

  #render() {
    const { savedText, checkboxLabel, checkbox } = this.refs;
    const checked = !!checkbox?.checked;
    const additional = this.#additionalSaved();
    const totalSaved = this.#baseSaved() + (checked ? additional : 0);

    if (savedText) {
      const icon = savedText.querySelector('.super-save-banner__check');
      savedText.textContent = this.savedTemplate.replace('[amount]', this.#format(totalSaved));
      if (icon) savedText.prepend(icon);
      // Nothing has actually been saved yet (no compare-at price, and the
      // extra discount hasn't been ticked on) - don't show "$0.00 Saved!".
      savedText.hidden = totalSaved <= 0;
    }
    if (checkboxLabel) {
      checkboxLabel.textContent = this.checkboxTemplate.replace('[amount]', this.#format(additional));
    }

    this.#updatePriceDisplay(checked);
    this.#notifyBadge(checked);
  }

  /**
   * Tells the Flat Discount Badge block (if present) to show/hide its
   * "+ ADDITIONAL X% OFF" pill and keeps its text in sync. Dispatched on
   * every render so it's always current, including right after a
   * variant-switch re-render.
   * @param {boolean} checked
   */
  #notifyBadge(checked) {
    this.dispatchEvent(
      new CustomEvent('supersave:toggle', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          checked,
          percentage: this.discountPercentage,
          template: this.badgeTemplate,
        },
      })
    );
  }

  #updatePriceDisplay(checked) {
    const container = this.section || document;
    const priceEl = container.querySelector(`product-price[data-product-id="${this.dataset.productId}"] .price`);
    if (!(priceEl instanceof HTMLElement)) return;

    if (!priceEl.dataset.superSaveOriginal) {
      priceEl.dataset.superSaveOriginal = priceEl.textContent ?? '';
    }

    priceEl.textContent = checked
      ? this.#format(Math.max(0, this.price - this.#additionalSaved()))
      : priceEl.dataset.superSaveOriginal;
  }

  /**
   * Applies or removes the real discount code on the cart, then updates
   * the displayed savings to match. Optimistic: the visible state
   * updates immediately on click, before the network request resolves.
   * @param {Event} event
   */
  toggleDiscount = async (event) => {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const wantsChecked = checkbox.checked;
    const { errorMessage } = this.refs;

    errorMessage?.setAttribute('hidden', '');
    checkbox.disabled = true;

    // Optimistic update: checkbox.checked already reflects the new state
    // (the browser flips it before the `change` event fires), so render
    // immediately - badge, saved text, and price all update instantly
    // instead of waiting on the cart request's round trip. The real
    // discount is applied in the background below; on genuine failure we
    // revert and re-render.
    this.#render();

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const cartResponse = await fetch('/cart.js', { signal: abortController.signal });
      const cart = await cartResponse.json();
      // Shopify's /cart/update legitimately reports a perfectly valid,
      // active, storewide, no-minimum code as "not applicable" whenever
      // the cart is empty - there's simply nothing yet to discount. That's
      // expected on a product page (this block renders before Add to
      // Cart) and in the theme editor's demo preview, so it must not be
      // treated as an invalid-code error.
      const cartWasEmpty = cart.item_count === 0;
      const otherCodes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.filter(
            (/** @type {{code: string}} */ dc) => dc.code.toLowerCase() !== this.discountCode.toLowerCase()
          )
        : [];
      // Only one discount code can be used per order: another code that is really discounting the
      // cart blocks this one. A code that is sitting in the cart doing nothing (e.g. below its
      // minimum) is simply replaced.
      const activeOther = otherCodes.find((/** @type {{applicable: boolean}} */ dc) => dc.applicable !== false);
      if (wantsChecked && activeOther) {
        checkbox.checked = false;
        if (errorMessage) {
          errorMessage.textContent = `Only one discount code can be used per order. ${activeOther.code} is already applied.`;
          errorMessage.removeAttribute('hidden');
        }
        this.#render();
        return;
      }
      const existingCodes = wantsChecked ? [] : otherCodes.map((/** @type {{code: string}} */ dc) => dc.code);

      const targetCodes = wantsChecked ? [this.discountCode] : existingCodes;
      const deferredPromise = CartDiscountUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: targetCodes.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: targetCodes.join(',') }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const applied = Array.isArray(data.discount_codes)
        ? data.discount_codes.find(
            (/** @type {{code: string, applicable: boolean}} */ d) =>
              d.code.toLowerCase() === this.discountCode.toLowerCase()
          )
        : null;

      deferredPromise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });

      const failed = wantsChecked && (!applied || (applied.applicable === false && !cartWasEmpty));

      if (failed) {
        checkbox.checked = false;
        if (errorMessage) {
          errorMessage.textContent =
            'This code could not be applied. It may need a higher order value (see the cart for details).';
          errorMessage.removeAttribute('hidden');
        }
        this.#render();
        return;
      }

      checkbox.checked = wantsChecked;
      this.#render();
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        checkbox.checked = !wantsChecked;
        if (errorMessage) {
          errorMessage.textContent = 'Something went wrong applying this discount. Please try again.';
          errorMessage.removeAttribute('hidden');
        }
        this.dispatchEvent(
          new CartErrorEvent({ error: error.message || 'Failed to update discount', code: 'SERVICE_UNAVAILABLE' })
        );
        this.#render();
      }
    } finally {
      checkbox.disabled = false;
      this.#activeFetch = null;
    }
  };

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then((/** @type {any} */ { detail }) => {
        if (!detail?.html) return;

        const newEl = detail.html.querySelector(
          `super-save-banner-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newEl) return;

        if (typeof newEl.dataset.price === 'string') this.dataset.price = newEl.dataset.price;
        if (typeof newEl.dataset.compareAtPrice === 'string') this.dataset.compareAtPrice = newEl.dataset.compareAtPrice;

        this.#readDataset();
        this.#render();
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[super-save-banner] promise rejected:', error);
      });
  };
}

if (!customElements.get('super-save-banner-component')) {
  customElements.define('super-save-banner-component', SuperSaveBanner);
}
